import { zodResolver } from "@hookform/resolvers/zod";
import { MessageSource } from "@prisma/client";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { trpc } from "@calcom/trpc/react";
import {
  Button,
  Form,
  Label,
  Select,
  showToast,
  Switch,
  TextAreaField,
  TextField,
  Tooltip,
} from "@calcom/ui";

import { messageTemplateNames } from "@lib/constant";
import { formatNumber, formatPhoneNumber, parseMessageTemplate } from "@lib/practice/helper";
import { getDefaultPRTemplate, PRTemplateOptions } from "@lib/templates/paymentRequest/defaults";

import { DollarIcon, InformationIcon, MailIcon, MobilePhoneIcon } from "@components/Icons";
import { useOfficeContext } from "@components/navbar";
import { OfficePatientSelect } from "@components/patients/OfficePatientSelect";
import PaymentSuccessDialog from "@components/patients/PaymentSuccessDialog";

type FormValues = {
  amount: number;
  email: string;
  phone: string;
  onEmail: boolean;
  onText: boolean;
  message: string;
  subject: string;
  discountAmount: number;
  discountPercentage: number;
  showLedgerBreakdown: boolean;
};

type Props = {
  patientId?: number;
  officeId: number;
  officename?: string;
  setPaymentDialog: React.Dispatch<React.SetStateAction<boolean>>;
  createConversationRoom?: boolean;
};

export default function PaymentRequest({
  patientId,
  officeId,
  officename,
  setPaymentDialog,
  createConversationRoom,
}: Props) {
  const [loading, setLoading] = useState(false);
  const trpcUtil = trpc.useContext();

  const [patient, setPatient] = useState<number | undefined>(patientId);
  const [template, setTemplate] = useState<any>(PRTemplateOptions[0]);
  const [paymentLink, setPaymentLink] = useState<string>("");
  const [emailContent, setEmailContent] = useState<{
    patientName: string | undefined;
    officeName: string | undefined;
    officeNumber: string | undefined | null;
    logo: string | undefined | null;
    officeAddress: string | undefined | null;
    senderName: string | undefined;
    senderAddress: string | undefined | null;
    headerColor: string | undefined | null;
    statementLink: string | undefined;
    balanceAmount: number;
    officeEmail: string | undefined | null;
  }>({
    patientName: "",
    officeName: "",
    officeNumber: "",
    logo: "",
    officeAddress: "",
    senderName: "",
    senderAddress: "",
    headerColor: "",
    statementLink: "",
    balanceAmount: 0,
    officeEmail: "",
  });

  const [paymentSuccessDialogOpen, setPaymentSuccessDialogOpen] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: {
      amount: 0,
      email: "",
      phone: "",
      onEmail: true,
      onText: true,
      subject: "We're kindly requesting payment",
      discountAmount: 0,
      discountPercentage: 0,
      showLedgerBreakdown: true,
    },
    resolver: zodResolver(
      z
        .object({
          amount: z.number().min(0.01),
          email: z.string().email().or(z.literal("")),
          phone: z.string().or(z.literal("")),
          onEmail: z.boolean(),
          onText: z.boolean(),
          message: z.string().min(1, "Required"),
          subject: z.string().min(1, "Required"),
          discountAmount: z.number().optional(),
          discountPercentage: z.number().optional(),
          showLedgerBreakdown: z.boolean(),
        })
        .superRefine((values, ctx) => {
          if (values.onEmail && !values.email) {
            ctx.addIssue({
              message: "Please enter valid email address",
              code: z.ZodIssueCode.custom,
              path: ["email"],
            });
          }
          if (values.onText && !values.phone) {
            ctx.addIssue({
              message: "Please enter valid phone number",
              code: z.ZodIssueCode.custom,
              path: ["phone"],
            });
          }
          if (!values.onText && !values.onEmail) {
            ctx.addIssue({
              message: "Either phone or email should be filled in.",
              code: z.ZodIssueCode.custom,
              path: ["email"],
            });
          }
        })
    ),
  });

  const { register, control, setValue, watch, reset } = form;

  const watchShowAmount = watch("amount");
  const watchOnEmail = watch("onEmail");
  const watchOnText = watch("onText");

  const sendRequest = trpc.viewer.patients.sendPaymentRequest.useMutation({
    onSuccess: (data) => {
      setLoading(false);
      setPaymentSuccessDialogOpen(true);
      if (data && data.email.on) {
        showToast(data.email.status ? "Email sent" : "Email failed", data.email.status ? "success" : "error");
      }
      if (data && data.sms.on) {
        showToast(data.sms.status ? "SMS sent" : "SMS failed", data.sms.status ? "success" : "error");
      }
      if (!data) {
        showToast("Error occured", "error");
      }
      if (createConversationRoom) {
        trpcUtil.viewer.twaymessaging.roomList.invalidate();
        trpcUtil.viewer.twaymessaging.unreadTotal.invalidate();
      }
    },
    onError: (e) => {
      setLoading(false);
      showToast("Error occured", "error");
    },
  });

  const { data: singlePatient } = trpc.viewer.patients.getById.useQuery(
    {
      billingOfficeId: officeId,
      patientId: Number(patient),
    },
    { enabled: !!patient }
  );

  const { office } = useOfficeContext();

  const { data: savedTemplates } = trpc.viewer.messageCustomize.getTemplates.useQuery({
    officeId,
    source: MessageSource.PAYMENT_REQUEST,
  });

  const handleSubmit = (data: FormValues) => {
    if (!patient) {
      showToast("Please select patient", "error");
      return;
    }
    setLoading(true);
    sendRequest.mutate({
      ...data,
      billingOfficeId: officeId,
      patientId: patient,
      createConversationRoom,
      template: template.value,
    });
  };

  useEffect(() => {
    if (singlePatient) {
      setValue("amount", singlePatient.amountDue || 0);
      setValue("email", singlePatient.email || "");
      setValue(
        "phone",
        formatPhoneNumber(singlePatient.mobilePhones.length > 0 ? singlePatient.mobilePhones[0] : "")
      );
      setValue("onEmail", !!singlePatient.email);
      setValue("onText", !!singlePatient.phoneNumbers.length);
    }
    setPaymentLink(`${process.env.NEXT_PUBLIC_WEBAPP_URL}/${officename}/payment-link`);
  }, [singlePatient, officename]);

  useEffect(() => {
    setEmailContent({
      patientName: (singlePatient?.firstName || "Patient") + " " + (singlePatient?.lastName || "Name"),
      officeName: office?.name,
      officeNumber: office?.phone,
      logo: office?.logo,
      officeAddress: office?.address1,
      senderName: office?.name,
      senderAddress: office?.email,
      headerColor: office?.headerColor,
      statementLink: "(Payment link will pre-fill based on account selected)",
      balanceAmount: watchShowAmount,
      officeEmail: office?.email,
    });
  }, [singlePatient, officename, office, watchShowAmount]);

  useEffect(() => {
    if (!template) return;

    const templateName = template.value;

    const saved = savedTemplates?.find((t) => t.templateName === templateName && t.type === "EMAIL");
    const defaultTpl = getDefaultPRTemplate(templateName);

    const rawContent = saved?.content || defaultTpl?.content || "";
    const rawSubject = saved?.subject || defaultTpl?.subject || "";

    const patientFullName =
      (singlePatient?.firstName || "Patient") + " " + (singlePatient?.lastName || "Name");

    const templateData: Record<string, string> = {
      patientName: patientFullName,
      firstName: singlePatient?.firstName || "Patient",
      officeName: office?.name || "",
      officePhone: office?.phone || "",
      officeEmail: office?.email || "",
      amount: String(formatNumber(watchShowAmount, "balance")),
      paymentLink: paymentLink,
    };

    const parsedContent = parseMessageTemplate(rawContent, templateData);
    const parsedSubject = parseMessageTemplate(rawSubject, templateData);

    setValue("message", parsedContent);
    setValue("subject", parsedSubject);
  }, [emailContent, template, savedTemplates]);

  return (
    <div>
      <Form className="w-full" form={form} handleSubmit={handleSubmit}>
        <div className="space-y-3 pb-6">
          <div className="mt-6 flex flex-col gap-5">
            <div id="payment-request-account-select">
              <Label>Account</Label>
              <OfficePatientSelect
                officeId={officeId}
                value={patient}
                onChange={setPatient}
                placeholder="Search Account"
                className="rounded-xl"
                showMissingPatientHelp
              />
            </div>
            <div id="payment-request-templates-select">
              <Label>Templates</Label>
              <div className="relative w-full">
                <Select
                  placeholder="Search Template"
                  className="rounded-xl"
                  value={PRTemplateOptions.find((p) => p.value === template?.value)}
                  options={PRTemplateOptions}
                  onChange={(option) => {
                    if (option) {
                      setTemplate(option);
                    }
                  }}
                />
              </div>
            </div>
            <div id="payment-request-amount-input">
              <Label>Amount Requested</Label>
              <TextField
                type="number"
                className="relative rounded-lg"
                label=""
                step={0.01}
                min={0}
                addOnLeading={<DollarIcon />}
                {...register("amount", { valueAsNumber: true })}
              />
            </div>
            <div id="payment-request-email-input">
              <Controller
                name="onEmail"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="flex items-center justify-between">
                    <Label className="m-0 text-gray-600">Email Address</Label>
                    <Switch label="" checked={value} onCheckedChange={onChange} />
                  </div>
                )}
              />
              <div className="relative">
                <TextField
                  label=""
                  disabled={!watchOnEmail}
                  className="w-full"
                  addOnLeading={<MailIcon />}
                  {...register("email")}
                />
              </div>
            </div>
            <div id="payment-request-phone-input">
              <Controller
                name="onText"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="flex items-center justify-between">
                    <Label className="m-0 text-gray-600">Phone Number</Label>
                    <Switch label="" checked={value} onCheckedChange={onChange} />
                  </div>
                )}
              />
              <div className="relative">
                <TextField
                  label=""
                  disabled={!watchOnText}
                  className="w-full"
                  addOnLeading={<MobilePhoneIcon />}
                  {...register("phone")}
                  placeholder="(555) 555-5555"
                  onChange={(e) => setValue("phone", formatPhoneNumber(e.target.value))}
                />
              </div>
            </div>
            <div id="payment-request-subject-input">
              <Label className="flex items-center gap-2">
                Email Subject
                <Tooltip
                  content="The email subject will only appear when the message is sent as an email; it will not appear in a text message."
                  className="z-50 max-w-[500px]">
                  <span>
                    <InformationIcon />
                  </span>
                </Tooltip>
              </Label>
              <TextField
                label=""
                className="w-full"
                disabled={template?.value !== messageTemplateNames.PAYMENT_REQUEST_CUSTOM}
                {...register("subject")}
              />
            </div>
            <div id="payment-request-message-input" className="flex flex-col gap-2 md:col-span-1">
              <div className="w-full">
                <TextAreaField
                  label="Message Content"
                  rows={template?.value !== messageTemplateNames.PAYMENT_REQUEST_CUSTOM ? 15 : 5}
                  disabled={template?.value !== messageTemplateNames.PAYMENT_REQUEST_CUSTOM}
                  readOnly={template?.value !== messageTemplateNames.PAYMENT_REQUEST_CUSTOM}
                  className="w-full disabled:bg-gray-50"
                  {...register("message")}
                />
              </div>
              {template.value === messageTemplateNames.PAYMENT_REQUEST_CUSTOM && (
                <p className="text-sm text-gray-800">
                  *Payment link will pre-fill based on account selected at the end of the message
                </p>
              )}
            </div>
            <div id="payment-request-ledger-breakdown-switch">
              <Controller
                name="showLedgerBreakdown"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="flex items-center justify-between">
                    <Label className="m-0 text-gray-600">Include Ledger Breakdown</Label>
                    <Switch label="" checked={value} onCheckedChange={onChange} />
                  </div>
                )}
              />
            </div>
            <Button
              id="payment-request-submit-button"
              className="flex h-10 justify-center bg-black text-white hover:bg-black hover:text-white"
              type="submit"
              loadingTextClassName="text-black"
              loading={loading}>
              Send ${formatNumber(watchShowAmount, "balance")} Payment Request
            </Button>
          </div>
        </div>
      </Form>

      <PaymentSuccessDialog
        title="Payment Request Sent"
        description="Your payment request has been sent."
        amount={watchShowAmount}
        account={singlePatient?.firstName + " " + singlePatient?.lastName}
        date={new Date().toLocaleDateString()}
        source="Payment Request"
        open={paymentSuccessDialogOpen}
        handler={() => {
          setPaymentSuccessDialogOpen(false);
          reset();
          setPatient(undefined);
        }}
        onClose={() => {
          setPaymentSuccessDialogOpen(false);
          setPaymentDialog(false);
          setPatient(undefined);
          reset();
        }}
        email={singlePatient?.email || ""}
        phone={singlePatient?.phoneNumbers[0]?.number || ""}
        footer={
          <p className="text-center text-sm text-gray-500">
            The request was sent to {singlePatient?.firstName} {singlePatient?.lastName}&apos;s{" "}
            {singlePatient?.phoneNumbers[0]?.number ? singlePatient?.phoneNumbers[0]?.number + " and " : " "}
            {singlePatient?.email}
          </p>
        }
      />
    </div>
  );
}
